import { Grid, Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import HoverToolTip from '@src/components/HoverToolTip'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import { FormattedMessage } from 'react-intl'
import useFormikHelper from '../../useFormikHelper'
import {
  checkForRedesignedBiTubeProduct,
  shouldRenderApplyToNextOligo,
  shouldRenderSpecificationsHelpIcon,
  useProductFromQueryParam,
  useProductInput,
  applyToNextLabel,
} from '../../ProductsConfiguration'
import { handleOligoValidationAndPricing } from '../../utils'
import {
  useBiTubePriceCallMutation,
  useTubePriceCallMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import useTubeTotalValidation from '../../useTubeTotalValidation'

const useStyles = makeStyles((theme: Theme) => {
  return {
    specificationsHeaderContainer: {
      borderBottom: `1px solid ${theme.palette.grey[500]}`,
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(6),
    },

    applyToNextOligo: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    infoIcon: {
      fontSize: theme.typography.pxToRem(19.25),
      color: theme.palette.primary.main,
      cursor: 'pointer',
      marginLeft: theme.spacing(1.25),
      marginBottom: theme.spacing(-0.75),
    },
    checkBox: {
      fontSize: theme.typography.pxToRem(16),
    },
    titleStyle: {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: 900,
      textTransform: 'uppercase',
      display: 'inline',
    },
    disabledTitle: {
      color: theme.palette.grey[500],
    },
  }
})

const SpecificationsHeader = () => {
  const classes = useStyles()
  const formik = useFormikHelper()
  const product = useProductFromQueryParam()
  const productDetails = useProductInput()
  const { totalValidationAPI } = useTubeTotalValidation()
  const [tubePriceCall] = useTubePriceCallMutation()
  const [biTubePriceCall] = useBiTubePriceCallMutation()
  const renderSpecificationsHelpIcon =
    shouldRenderSpecificationsHelpIcon(product)
  const isBiTubeProduct = checkForRedesignedBiTubeProduct(product)
  const renderApplyToNextOligo = shouldRenderApplyToNextOligo(product)
  const isSequenceValid =
    formik.selectedOligoValues.sequence !== '' &&
    (!formik.errors || formik.errors?.sequence === '')
  const priceCall = isBiTubeProduct ? biTubePriceCall : tubePriceCall
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      formik.setFieldValue('applyToNextOligos', true, false)
      const {
        scale = '',
        purification = '',
        format = 'DRY',
        concentration = '',
        otherValue = '',
        tubes = '1',
        enabledConcentrationData = [],
        enabledPurificationData = [],
        enabledScaleData = [],
        enabledFormatData = [],
        listNumberOfTubes = [],
        amount = '',
        additionalServices = [],
        listWarning = [],
      } = formik.allValues.oligoList[0]
      const updatedValues = formik.allValues.oligoList.map((oligo, index) => ({
        ...oligo,
        scale: scale,
        purification: purification,
        format: format,
        concentration: concentration,
        otherValue: otherValue,
        tubes: tubes,
        enabledConcentrationData: enabledConcentrationData,
        enabledPurificationData: enabledPurificationData,
        enabledScaleData: enabledScaleData,
        enabledFormatData: enabledFormatData,
        listNumberOfTubes: listNumberOfTubes,
        amount: amount,
        additionalServices,
        listWarning: index === 0 ? listWarning : [],
        applyToAll: index === 0 ? true : false,
      }))
      formik.setFieldValue('oligoList', updatedValues, false)
      handleOligoValidationAndPricing(
        product,
        updatedValues,
        formik,
        productDetails,
        totalValidationAPI,
        priceCall,
        true
      )
    } else {
      formik.setFieldValue('applyToNextOligos', false, false)
    }
  }

  const displayApplyToNextOligos =
    formik.activeOligoIndex === 0 && renderApplyToNextOligo

  const applyToNext = applyToNextLabel(product)
  return (
    <Grid container className={classes.specificationsHeaderContainer}>
      <Grid item xs={6}>
        <Typography
          className={`${classes.titleStyle} ${
            !isSequenceValid ? classes.disabledTitle : ''
          }`}
        >
          <FormattedMessage
            id="CONF_PEPSCREEN_SPECIFICATION"
            defaultMessage="Specifications"
          />
        </Typography>
        {renderSpecificationsHelpIcon && (
          <HoverToolTip
            fontSize="19.25px"
            toolTipText={
              <FormattedMessage
                id="CONF_SPECIFICATION_GREYED_OUT_MESSAGE"
                defaultMessage="Greyed out options are unavailable based upon your sequence."
              />
            }
          />
        )}
      </Grid>
      {displayApplyToNextOligos && (
        <Grid item xs={6} className={classes.applyToNextOligo}>
          <LiquidCheckbox
            disabled={!isSequenceValid}
            onChange={handleChange}
            checked={formik.allValues.applyToNextOligos}
            label={applyToNext}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default SpecificationsHeader
